import { useLazyQuery, useQuery } from '@apollo/client';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FORM_PAGES } from '../../../components/Form/types';
import FormCheckbox from '../../../components/FormCheckbox/FormCheckbox';
import { NewPetButton } from '../../../components/FormCheckbox/styled';
import { FORM_CHECKBOX_TYPES } from '../../../components/FormCheckbox/types';
import { FORM_INFO_SLOT_TYPES } from '../../../components/FormInfo/types';
import { CenteredLoadingSpinner } from '../../../components/Shared/LoadingSpinner/LoadingSpinner';
import PlusSvg from '../../../images/plus.svg';
import { vars } from '../../../reactive';
import { setFormPage } from '../../../reactive/actions';
import { BranchForm } from '../../../types';
import { AnalyticsEvents, trackEvent } from '../../../utils/analytics';
import { GetAllPets, GetRequiredForms } from '../../../utils/queries';

const FormCheckboxPet = () => {
  const { t } = useTranslation();
  const currentBranch = vars.currentBranch();
  const petTypes = currentBranch?.BranchTargeting?.pet_type;
  const isMoreThanOnePetType = petTypes?.length > 1;
  const newPet = vars.newPet();
  const type = FORM_CHECKBOX_TYPES.SELECT_PETS;
  const formOptions = useForm();
  const product = vars.selectedProduct()!;
  const watchedValue = formOptions.watch(`checkbox.${type}`) || [];
  const { loading: loadingPets, data: userProfile = {} as { getProfile: Queries.CollarAPI_AppUser } } = useQuery<{ getProfile: Queries.CollarAPI_AppUser }>(GetAllPets, {
    fetchPolicy: 'no-cache'
  });

  const [getRequiredForms, { loading: loadingRequiredForms, data: requiredForms }] = useLazyQuery<{ getRequiredFormsForOrder: BranchForm[] }>(GetRequiredForms, {
    fetchPolicy: 'network-only'
  });

  const loadedRequiredForms = requiredForms?.getRequiredFormsForOrder !== undefined;

  const allPets = useMemo(() => {
    if (userProfile?.getProfile?.id) {
      return [...userProfile.getProfile.Pets!, ...userProfile.getProfile.ChappedPets!];
    }
    return [];
  }, [userProfile]);
  const loadedPets = !!allPets.length;

  const onSubmit = (data: Record<string, any>) => {
    if (!Array.isArray(data.checkbox[type])) {
      data.checkbox[type] = [data.checkbox[type]];
    }
    const selected = data.checkbox[type].length && data.checkbox[type].map((id: number) => allPets.find((pet: any) => pet.id === id));

    trackEvent(AnalyticsEvents.PETS_SELECTED, {
      count: selected?.length
    });

    vars.selectedPets(selected);
    vars.formsAnswers([]);

    if (vars.isFormFillView()) {
      setFormPage(FORM_PAGES.BOOKING_FORMS);
      return;
    }

    getRequiredForms({ variables: { ProductId: product.id, PetRecordId: selected?.map((pet: any) => pet.PetRecord?.id) } });
  };

  useEffect(() => {
    if (!loadedRequiredForms) {
      return;
    }

    if (requiredForms?.getRequiredFormsForOrder?.length) {
      vars.selectedForms(requiredForms?.getRequiredFormsForOrder);
      setFormPage(FORM_PAGES.BOOKING_FORMS);
      return;
    }

    setFormPage(FORM_PAGES.BOOKING_SUMMARY);
  }, [loadedRequiredForms]);

  const loading = loadingPets || loadingRequiredForms;

  const isPetAllowed = (pet: any) => {
    const allowedPetTypes = product?.pet_rules?.allowed_pet_types ?? (petTypes?.length ? petTypes : ['DOG', 'CAT', 'RABBIT', 'HORSE', 'DUCK', 'GUINEA_PIG']);

    const isPetTypeAllowed = allowedPetTypes.includes(pet.type);
    const excludedPetBreeds = product?.pet_rules?.excluded_pet_breeds ?? [];
    const isPetBreedAllowed = !excludedPetBreeds.includes(pet.BreedId);
    const minAge = product?.pet_rules?.pet_min_age ? Number(product?.pet_rules?.pet_min_age) : 0;
    const maxAge = product?.pet_rules?.pet_max_age ? Number(product?.pet_rules?.pet_max_age) : Infinity;
    const petAge = new Date().getFullYear() - new Date(pet?.birthdate).getFullYear();
    const neuteredOnly = product?.pet_rules?.neutered_only;
    const isNeutered = pet?.PetRecord?.neutred;
    const isNeuteredAllowed = !neuteredOnly || neuteredOnly === 'no_rule' || (neuteredOnly === 'neutered' && isNeutered) || (neuteredOnly === 'not_neutered' && !isNeutered);
    const isPetAgeAllowed = petAge >= minAge && petAge <= maxAge;
    const maxPetCount = Number(product?.pet_rules?.max_pet_count) || Infinity;
    const isPetCountAllowed = watchedValue?.length < maxPetCount;
    return isPetTypeAllowed && isPetBreedAllowed && isPetAgeAllowed && isPetCountAllowed && isNeuteredAllowed;
  };

  return (
    <>
      {loadedPets && (
        <FormCheckbox
          type={type}
          formOptions={formOptions}
          onSubmit={onSubmit}
          checkboxData={allPets.map((pet: any) => ({
            id: pet.id,
            name: pet.name,
            disabled: !isPetAllowed(pet),
            pic: pet?.profile_pic
          }))}
          previousPage={{
            onPrevious: () => {
              const multiSlotProduct = product?.booking_type === FORM_INFO_SLOT_TYPES.MULTI_SLOT;

              if (vars.isFormFillView()) {
                setFormPage(FORM_PAGES.FORM_SELECT);
                return;
              }

              if (!multiSlotProduct && product?.type !== 'subscription') {
                setFormPage(FORM_PAGES.BOOKING_CALENDAR_DATE_AND_TIME);
                return;
              }

              let newPage = FORM_PAGES.PRODUCTS;

              if (Object.values(product?.customizations || {})?.length) {
                newPage = FORM_PAGES.PRODUCT_CUSTOMIZATIONS;
              }

              setFormPage(newPage);
              return;
            },
            animation: 'fade-out'
          }}
          loading={loading}
        >
          <NewPetButton
            type="button"
            onClick={() => {
              if (isMoreThanOnePetType) {
                setFormPage(FORM_PAGES.CREATE_PET_TYPE, 'fade-out');
              } else {
                vars.newPet({ ...newPet, type: petTypes[0] });
                setFormPage(FORM_PAGES.CREATE_PET_CREATE_PET);
              }
            }}
          >
            <div>
              <PlusSvg />
            </div>
            <span>{t('addNewPet')}</span>
          </NewPetButton>
        </FormCheckbox>
      )}
      {!loadedPets && <CenteredLoadingSpinner />}
    </>
  );
};
export default FormCheckboxPet;
